import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

function PhotoGalleryModal({
  stopId,
  isOpen,
  onClose,
  allowDelete = false,
  title = 'Service Photos'
}) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadPhotos = useCallback(async () => {
    if (!stopId) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/route-stops/${stopId}/photos`
      );
      setPhotos(response.data);
    } catch (err) {
      console.error('Failed to load photos:', err);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [stopId]);

  useEffect(() => {
    if (isOpen && stopId) {
      loadPhotos();
      setSelectedIndex(null);
    }
  }, [isOpen, stopId, loadPhotos]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (selectedIndex !== null) {
        if (e.key === 'ArrowLeft') {
          setSelectedIndex(prev => (prev > 0 ? prev - 1 : photos.length - 1));
        } else if (e.key === 'ArrowRight') {
          setSelectedIndex(prev => (prev < photos.length - 1 ? prev + 1 : 0));
        } else if (e.key === 'Escape') {
          setSelectedIndex(null);
        }
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, photos.length, onClose]);

  const handleDelete = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    setDeleting(true);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      if (selectedIndex !== null && photos.length <= 1) {
        setSelectedIndex(null);
      } else if (selectedIndex !== null && selectedIndex >= photos.length - 1) {
        setSelectedIndex(photos.length - 2);
      }
    } catch (err) {
      console.error('Failed to delete photo:', err);
      alert('Failed to delete photo');
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Gallery Modal */}
      <div className="photo-gallery-overlay" onClick={onClose}>
        <div className="photo-gallery-modal" onClick={e => e.stopPropagation()}>
          <div className="photo-gallery-header">
            <h3>{title}</h3>
            <button className="photo-gallery-close" onClick={onClose}>&times;</button>
          </div>

          <div className="photo-gallery-content">
            {loading ? (
              <div className="photo-gallery-loading">Loading photos...</div>
            ) : photos.length === 0 ? (
              <div className="photo-gallery-empty">No photos available</div>
            ) : (
              <div className="photo-gallery-grid">
                {photos.map((photo, index) => (
                  <div key={photo.id} className="photo-gallery-item">
                    <img
                      src={photo.photo_url}
                      alt={photo.caption || 'Service photo'}
                      onClick={() => setSelectedIndex(index)}
                    />
                    {allowDelete && (
                      <button
                        className="photo-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(photo.id);
                        }}
                        disabled={deleting}
                        title="Delete photo"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full-size Photo View */}
      {selectedIndex !== null && photos[selectedIndex] && (
        <div className="photo-fullsize-overlay" onClick={() => setSelectedIndex(null)}>
          <button className="photo-fullsize-close" onClick={() => setSelectedIndex(null)}>&times;</button>

          {photos.length > 1 && (
            <>
              <button
                className="photo-nav-btn photo-nav-prev"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedIndex(prev => (prev > 0 ? prev - 1 : photos.length - 1));
                }}
              >
                &#8249;
              </button>
              <button
                className="photo-nav-btn photo-nav-next"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedIndex(prev => (prev < photos.length - 1 ? prev + 1 : 0));
                }}
              >
                &#8250;
              </button>
            </>
          )}

          <img
            src={photos[selectedIndex].photo_url}
            alt={photos[selectedIndex].caption || 'Service photo'}
            className="photo-fullsize-img"
            onClick={e => e.stopPropagation()}
          />

          <div className="photo-fullsize-info" onClick={e => e.stopPropagation()}>
            <span className="photo-counter">{selectedIndex + 1} / {photos.length}</span>
            {photos[selectedIndex].caption && (
              <span className="photo-caption">{photos[selectedIndex].caption}</span>
            )}
            {allowDelete && (
              <button
                className="photo-fullsize-delete"
                onClick={() => handleDelete(photos[selectedIndex].id)}
                disabled={deleting}
              >
                Delete Photo
              </button>
            )}
          </div>
        </div>
      )}

      <style>{`
        .photo-gallery-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 200;
          padding: 20px;
        }
        .photo-gallery-modal {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 600px;
          max-height: 80vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .photo-gallery-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #e2e8f0;
        }
        .photo-gallery-header h3 {
          margin: 0;
          font-size: 1.1rem;
          font-weight: 600;
        }
        .photo-gallery-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: #64748b;
          padding: 0;
          line-height: 1;
        }
        .photo-gallery-close:hover {
          color: #1e293b;
        }
        .photo-gallery-content {
          padding: 20px;
          overflow-y: auto;
        }
        .photo-gallery-loading,
        .photo-gallery-empty {
          text-align: center;
          color: #64748b;
          padding: 40px 20px;
        }
        .photo-gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          gap: 12px;
        }
        .photo-gallery-item {
          position: relative;
          aspect-ratio: 1;
          border-radius: 8px;
          overflow: hidden;
          cursor: pointer;
        }
        .photo-gallery-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.15s;
        }
        .photo-gallery-item:hover img {
          transform: scale(1.05);
        }
        .photo-delete-btn {
          position: absolute;
          top: 6px;
          right: 6px;
          background: rgba(255, 255, 255, 0.9);
          border: none;
          border-radius: 4px;
          padding: 4px 6px;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.15s;
          font-size: 0.8rem;
        }
        .photo-gallery-item:hover .photo-delete-btn {
          opacity: 1;
        }
        .photo-delete-btn:hover {
          background: #fee2e2;
        }

        /* Full-size overlay */
        .photo-fullsize-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.95);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 300;
        }
        .photo-fullsize-close {
          position: absolute;
          top: 20px;
          right: 20px;
          background: none;
          border: none;
          color: white;
          font-size: 2rem;
          cursor: pointer;
          z-index: 301;
        }
        .photo-fullsize-img {
          max-width: 90vw;
          max-height: 80vh;
          object-fit: contain;
        }
        .photo-nav-btn {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          background: rgba(255, 255, 255, 0.1);
          border: none;
          color: white;
          font-size: 3rem;
          padding: 20px 15px;
          cursor: pointer;
          z-index: 301;
          transition: background 0.15s;
        }
        .photo-nav-btn:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        .photo-nav-prev {
          left: 10px;
        }
        .photo-nav-next {
          right: 10px;
        }
        .photo-fullsize-info {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          gap: 16px;
          color: white;
        }
        .photo-counter {
          background: rgba(0, 0, 0, 0.5);
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 0.9rem;
        }
        .photo-caption {
          background: rgba(0, 0, 0, 0.5);
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 0.9rem;
          max-width: 300px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .photo-fullsize-delete {
          background: #dc2626;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.9rem;
        }
        .photo-fullsize-delete:hover {
          background: #b91c1c;
        }
        .photo-fullsize-delete:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 640px) {
          .photo-gallery-grid {
            grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
            gap: 8px;
          }
          .photo-nav-btn {
            font-size: 2rem;
            padding: 15px 10px;
          }
          .photo-fullsize-info {
            flex-direction: column;
            gap: 8px;
          }
        }
      `}</style>
    </>
  );
}

export default PhotoGalleryModal;
